// Minimal markdown -> HTML for the AI agent panel.
//
// Handles: headings, paragraphs, bold/italic, inline code, code blocks,
// links, nested ordered/unordered lists, blockquotes, horizontal rules,
// and pipe-style tables.
//
// The output is rendered with v-html, and the input is untrusted LLM
// output (tool results can echo back account names, notes, etc). So we
// escape all raw HTML and only ever emit a fixed set of tags. Link hrefs
// are passed through safeHref(), which drops anything that is not http,
// https, mailto, or a same-origin path. That blocks javascript: URLs.

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Allow only safe URL schemes. Anything else (javascript:, data:, vbscript:,
// protocol-relative //host, or a mangled value) collapses to '#' so a click
// does nothing. Quotes are percent-encoded so the value can never break out
// of an HTML attribute.
export function safeHref(raw) {
  const href = String(raw).trim()
  const isInternalPath = href.startsWith('/') && !href.startsWith('//')
  if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href) || isInternalPath) {
    return href.replace(/"/g, '%22').replace(/'/g, '%27')
  }

  return '#'
}

const HEADING_RE = /^#{1,6}\s/
const BULLET_RE = /^(\s*)[-*]\s+/
const NUMBER_RE = /^(\s*)\d+\.\s+/
const QUOTE_RE = /^>\s?/
const RULE_RE = /^(-{3,}|\*{3,}|_{3,})\s*$/

// A currency amount, a plain number, a percentage, or a dash placeholder.
// Used to right-align amount columns the model did not mark with `---:`.
const NUMERIC_RE = /^-?\$?\d[\d,]*(\.\d+)?%?$|^-?\$\d[\d,]*(\.\d+)?$|^[-–—]$/

// Any line that opens a new block. The paragraph reader stops here, so adding
// a block type in one place keeps paragraphs from swallowing it.
function isBlockStart(line) {
  return line.startsWith('```') ||
    line.startsWith('|') ||
    HEADING_RE.test(line) ||
    BULLET_RE.test(line) ||
    NUMBER_RE.test(line) ||
    QUOTE_RE.test(line) ||
    RULE_RE.test(line)
}

function inline(text) {
  let out = escapeHtml(text)
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>')
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, href) => {
    return `<a href="${safeHref(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`
  })
  return out
}

function splitRow(row) {
  return row.replace(/^\|/, '').replace(/\|$/, '').split('|').map(s => s.trim())
}

function renderTable(rows) {
  const header = splitRow(rows[0])
  const marks = splitRow(rows[1])
  const body = rows.slice(2).map(splitRow)

  // An explicit `:` in the separator wins. Otherwise a column whose cells are
  // all amounts is right-aligned, which is what makes billing tables readable.
  const columns = header.map((_, i) => {
    const mark = marks[i] || ''
    if (mark.startsWith(':') && mark.endsWith(':')) {
      return { align: 'center', numeric: false }
    }

    if (mark.endsWith(':')) {
      return { align: 'right', numeric: true }
    }

    const cells = body.map(r => r[i]).filter(c => c !== undefined && c !== '')
    const numeric = cells.length > 0 && cells.every(c => NUMERIC_RE.test(c.replace(/\*\*/g, '')))
    return { align: numeric ? 'right' : 'left', numeric }
  })

  const cell = (tag, value, i) => {
    const col = columns[i] || { align: 'left', numeric: false }
    const cls = col.numeric ? ' class="ai-md-num"' : ''
    return `<${tag}${cls} style="text-align:${col.align}">${inline(value)}</${tag}>`
  }

  const thead = '<thead><tr>' + header.map((h, i) => cell('th', h, i)).join('') + '</tr></thead>'
  const tbody = '<tbody>' + body.map(row =>
    '<tr>' + row.map((c, i) => cell('td', c, i)).join('') + '</tr>'
  ).join('') + '</tbody>'

  return `<div class="ai-md-table-wrap"><table class="ai-md-table">${thead}${tbody}</table></div>`
}

// Consumes a run of list lines and emits nested <ul>/<ol> by leading indent.
// Two spaces (or a tab) per level, which is what the model produces.
function renderList(lines, start) {
  const stack = []
  const out = []
  let i = start

  const close = (toDepth) => {
    while (stack.length > toDepth) {
      out.push(`</li></${stack.pop()}>`)
    }
  }

  while (i < lines.length) {
    const line = lines[i]
    const match = BULLET_RE.exec(line) || NUMBER_RE.exec(line)
    if (!match) {
      break
    }

    const tag = BULLET_RE.test(line) ? 'ul' : 'ol'
    const depth = Math.floor(match[1].replace(/\t/g, '  ').length / 2) + 1
    const text = line.slice(match[0].length)

    if (depth > stack.length) {
      // Open one level at a time; a jump from depth 1 to 3 still nests cleanly.
      while (stack.length < depth) {
        out.push(`<${tag} class="ai-md-list">`)
        stack.push(tag)
        if (stack.length < depth) {
          out.push('<li>')
        }
      }

      out.push('<li>')
    } else {
      close(depth)
      out.push('</li><li>')
    }

    out.push(inline(text))
    i++
  }

  close(0)
  return { html: out.join(''), next: i }
}

export function renderMarkdown(input) {
  if (!input) {
    return ''
  }

  const lines = input.split('\n')
  const out = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim()
      const codeLines = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }

      i++
      out.push(`<pre class="ai-md-code"><code data-lang="${escapeHtml(lang)}">${escapeHtml(codeLines.join('\n'))}</code></pre>`)
      continue
    }

    if (RULE_RE.test(line)) {
      out.push('<hr class="ai-md-rule">')
      i++
      continue
    }

    if (HEADING_RE.test(line)) {
      // h1..h3 become h3..h5 so the panel keeps one heading scale; deeper
      // levels clamp to h6 rather than emitting invalid tags.
      const level = Math.min(line.match(/^#+/)[0].length + 2, 6)
      out.push(`<h${level}>${inline(line.replace(/^#+\s+/, ''))}</h${level}>`)
      i++
      continue
    }

    if (line.startsWith('|') && lines[i + 1] && lines[i + 1].startsWith('|') && /^[|\s:-]+$/.test(lines[i + 1])) {
      const tableRows = []
      while (i < lines.length && lines[i].startsWith('|')) {
        tableRows.push(lines[i])
        i++
      }

      out.push(renderTable(tableRows))
      continue
    }

    if (QUOTE_RE.test(line)) {
      const quoted = []
      while (i < lines.length && QUOTE_RE.test(lines[i])) {
        quoted.push(lines[i].replace(QUOTE_RE, ''))
        i++
      }

      out.push(`<blockquote class="ai-md-quote">${inline(quoted.join(' '))}</blockquote>`)
      continue
    }

    if (BULLET_RE.test(line) || NUMBER_RE.test(line)) {
      const list = renderList(lines, i)
      out.push(list.html)
      i = list.next
      continue
    }

    if (line.trim() === '') {
      i++
      continue
    }

    const paraLines = [lines[i]]
    i++
    while (i < lines.length && lines[i].trim() !== '' && !isBlockStart(lines[i])) {
      paraLines.push(lines[i])
      i++
    }

    out.push(`<p>${inline(paraLines.join(' '))}</p>`)
  }

  return out.join('\n')
}
